import { useEffect, useState, type ReactElement } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Crown, Shield, UserRound, UsersRound } from 'lucide-react'
import { api } from '@/util/api'
import { AdminLayout } from '@/components/admin/AdminLayout'
import {
  Button,
  Notice,
  Select,
  Skeleton,
  SkeletonGroup,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHeader,
  TableHeaderCell,
  TableRow,
  useToast,
  type SelectItem,
} from '@/components/play'
import { useAdminOrganizations } from '@/hooks/useAdminOrganizations'
import { assignableRolesFor } from '@/lib/adminCapabilities'
import type { TeamResource } from '@/types/admin'

interface UserResource {
  id: string
  type: 'user'
  attributes: {
    id: string
    email: string
    first_name: string | null
    last_name: string | null
    full_name: string
    role: string
    team_id: string | null
    confirmed: boolean
    last_sign_in_at: string | null
  }
}

/** Each role keeps one glyph in the filter, the row pickers and the chosen value, so a role reads at a glance. */
const ROLE_DETAILS: Record<string, { label: string; icon: ReactElement; description: string }> = {
  user: { label: 'User', icon: <UserRound />, description: 'Views the organization’s events.' },
  manager: { label: 'Manager', icon: <UsersRound />, description: 'Creates and runs events.' },
  admin: { label: 'Admin', icon: <Shield />, description: 'Also manages users and teams.' },
  superadmin: { label: 'Superadmin', icon: <Crown />, description: 'Runs the whole platform.' },
}

function roleItem(role: string, disabled?: boolean): SelectItem<string> {
  const details = ROLE_DETAILS[role]
  return details ? { value: role, ...details, disabled } : { value: role, label: role, disabled }
}

export function AdminUsersPage() {
  const [users, setUsers] = useState<UserResource[]>([])
  const [teams, setTeams] = useState<TeamResource[]>([])
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const { toast } = useToast()
  const [searchParams] = useSearchParams()
  const [roleFilter, setRoleFilter] = useState<string>('all')
  const [teamFilter, setTeamFilter] = useState<string>('all')
  const [orgFilter, setOrgFilter] = useState<string>(searchParams.get('organization_id') || 'all')
  const { organizations, isSuperadmin } = useAdminOrganizations()
  const roleOptions = assignableRolesFor(isSuperadmin ? 'superadmin' : 'admin')

  useEffect(() => {
    loadData()
  }, [roleFilter, teamFilter, orgFilter])

  async function loadData() {
    setLoading(true)
    setLoadFailed(false)
    try {
      const params: Record<string, string> = {}
      if (orgFilter !== 'all') params.organization_id = orgFilter
      if (roleFilter !== 'all') params.role = roleFilter
      if (teamFilter !== 'all') params.team_id = teamFilter
      const teamParams = orgFilter !== 'all' && isSuperadmin ? { organization_id: orgFilter } : undefined

      const [usersRes, teamsRes] = await Promise.all([
        api.getAdminUsers(params),
        api.getAdminTeams(teamParams),
      ])
      setUsers((usersRes as { data: UserResource[] }).data)
      setTeams(teamsRes.data)
    } catch {
      setLoadFailed(true)
    } finally {
      setLoading(false)
    }
  }

  /** Saves one change to a user. The row shows the server's value again either way; a failure says so. */
  async function updateUser(userId: string, changes: { role?: string; team_id?: string | null }, failure: string) {
    try {
      await api.updateAdminUser(userId, changes)
    } catch (err: any) {
      toast({ title: failure, description: err?.response?.data?.error?.message || 'Try again.', tone: 'error' })
    }
    loadData()
  }

  async function handleRoleChange(userId: string, newRole: string | null) {
    if (!newRole || !roleOptions.includes(newRole)) return
    await updateUser(userId, { role: newRole }, 'Role not changed')
  }

  async function handleTeamChange(userId: string, teamId: string | null) {
    if (!teamId) return
    await updateUser(userId, { team_id: teamId === 'none' ? null : teamId }, 'Team not changed')
  }

  const teamItems: SelectItem<string>[] = teams.map((team) => ({ value: team.id, label: team.attributes.name }))
  const roleItems = roleOptions.map((role) => roleItem(role))

  /** The assignable roles. A role this admin can't assign stays visible, disabled, instead of an empty picker. */
  function roleItemsFor(role: string) {
    return roleOptions.includes(role) ? roleItems : [...roleItems, roleItem(role, true)]
  }

  /** No team plus the loaded teams. A team missing from the loaded list shows as "-" instead of its id. */
  function teamItemsFor(teamId: string | null) {
    const items: SelectItem<string>[] = [{ value: 'none', label: 'No team' }, ...teamItems]
    if (teamId && !teams.some((t) => t.id === teamId)) items.push({ value: teamId, label: '-', disabled: true })
    return items
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <h1 className="text-3xl font-semibold">Users</h1>

        <div className="flex flex-wrap gap-2">
          {isSuperadmin && organizations.length > 0 && (
            <Select
              className="w-[180px]"
              label="Organization"
              items={[
                { value: 'all', label: 'All organizations' },
                ...organizations.map((org) => ({ value: org.id, label: org.attributes.name })),
              ]}
              value={orgFilter}
              onValueChange={(v) => setOrgFilter(v ?? 'all')}
            />
          )}

          <Select
            className="w-[184px]"
            label="Role"
            items={[{ value: 'all', label: 'All roles' }, ...roleItems.map(({ value, label, icon }) => ({ value, label, icon }))]}
            value={roleFilter}
            onValueChange={(v) => setRoleFilter(v ?? 'all')}
          />

          <Select
            className="w-[160px]"
            label="Team"
            items={[{ value: 'all', label: 'All teams' }, ...teamItems]}
            value={teamFilter}
            onValueChange={(v) => setTeamFilter(v ?? 'all')}
          />
        </div>

        {loading ? (
          <SkeletonGroup label="Loading users" className="grid gap-2">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12" />)}
          </SkeletonGroup>
        ) : loadFailed ? (
          <Notice tone="error" action={<Button variant="secondary" size="compact" onClick={() => void loadData()}>Try again</Button>}>
            We couldn’t load users.
          </Notice>
        ) : (
          <Table caption="Users" hideCaption>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Email</TableHeaderCell>
                <TableHeaderCell>Role</TableHeaderCell>
                <TableHeaderCell>Team</TableHeaderCell>
                <TableHeaderCell>Last sign-in</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableHeaderCell scope="row">{user.attributes.full_name}</TableHeaderCell>
                  <TableCell className="text-neutral-7">{user.attributes.email}</TableCell>
                  <TableCell>
                    <Select
                      className="w-[184px]"
                      size="compact"
                      label={`Role for ${user.attributes.full_name}`}
                      hideLabel
                      items={roleItemsFor(user.attributes.role)}
                      value={user.attributes.role}
                      onValueChange={(v) => handleRoleChange(user.id, v)}
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      className="w-[160px]"
                      size="compact"
                      label={`Team for ${user.attributes.full_name}`}
                      hideLabel
                      items={teamItemsFor(user.attributes.team_id)}
                      value={user.attributes.team_id || 'none'}
                      onValueChange={(v) => handleTeamChange(user.id, v)}
                    />
                  </TableCell>
                  <TableCell type="data" className="text-neutral-7">
                    {user.attributes.last_sign_in_at
                      ? new Date(user.attributes.last_sign_in_at).toLocaleDateString()
                      : 'Never'}
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableEmpty colSpan={5}>No users match these filters</TableEmpty>
              )}
            </TableBody>
          </Table>
        )}
      </div>
    </AdminLayout>
  )
}
